import type {Moment} from 'moment-timezone'
import moment from 'moment-timezone'
import type {DatePickerProps} from 'react-datepicker'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import {Label} from '@freckle/educator-materials/ts/components/label'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {dateRangeContainer, middleText, dateRangeLabel} from './date-range-picker.module.scss'

type Props = {
  startDate: Moment
  endDate: Moment
  minDate?: Moment
  maxDate?: Moment
  onChangeStartDate: (date: Moment) => void
  onChangeEndDate: (date: Moment) => void
}

type DatePickerParams = Pick<DatePickerProps, 'selected'> & {
  placeholderI18nKey: string
  onChange: (date: Date | null) => void
  minDate?: Moment
  maxDate?: Moment
}

function useDatePickerProps(params: DatePickerParams): DatePickerProps {
  const {placeholderI18nKey, maxDate, minDate, onChange, selected} = params
  const {t} = useSafeTranslation()

  return {
    className: 'btn btn-default',
    selected,
    onChange,
    placeholderText: t(placeholderI18nKey),
    minDate: minDate?.utc().toDate(),
    maxDate: maxDate?.utc().toDate(),
  }
}

export function DateRangePicker(props: Props) {
  const {startDate, endDate, onChangeStartDate, onChangeEndDate, minDate, maxDate} = props
  const {t} = useSafeTranslation()

  const startDatePickerProps = useDatePickerProps({
    onChange: date => {
      if (date !== null) onChangeStartDate(moment(date).utc())
    },
    selected: startDate.utc().toDate(),
    placeholderI18nKey: 'DATE_RANGE_PICKER_START_PLACEHOLDER',
    minDate,
    maxDate,
  })

  const endDatePickerProps = useDatePickerProps({
    onChange: date => {
      if (date !== null) onChangeEndDate(moment(date).utc())
    },
    selected: endDate.utc().toDate(),
    placeholderI18nKey: 'DATE_RANGE_PICKER_END_PLACEHOLDER',
    minDate,
    maxDate,
  })

  return (
    <div className={`hidden-print ${dateRangeContainer}`}>
      <Label className={dateRangeLabel}>{t('DATE_RANGE_PICKER_LABEL')}</Label>
      <div>
        <div className={'btn-group'}>
          <DatePicker {...startDatePickerProps} />
        </div>
        <span className={middleText}>{t('DATE_RANGE_PICKER_RANGE_CONNECTIVE')}</span>
        <div className={'btn-group'}>
          <DatePicker {...endDatePickerProps} />
        </div>
      </div>
    </div>
  )
}
